import docsMutation from '@/api/mutitations/docs';
import PageTemplate from '@/components/PageTemplate';
import useAccessToken from '@/hooks/useAccessToken';
import Table from '@/components/Table';
import ChartComponent from '@/components/Chart';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import agentQuery from '@/api/queries/agent';
import { isObjectEmpty } from '@/helpers/is';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import exportRuleQuery from '@/api/queries/exportRule';
import { notifications } from '@mantine/notifications';
import prepareTranslate from '@/helpers/dictionary';
import { objectToQueryString, queryStringToObject, formatMonthYearString } from '@/helpers/string';
import { routeWithParams } from '@/helpers/route';
import routes from '@/routes';

const ReportView = () => {
  const t = prepareTranslate();
  useAccessToken();

  const [identifierId, setIdentifierId] = useState();
  const [columns, setColumns] = useState([]);
  const [noIdentifiers, setNoIdentifiers] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState();
  const [selectedProducts, setselectedProducts] = useState();
  const [selectedDate, setSelectedDate] = useState('');
  const [filterOptions, setFilterOptions] = useState({ product: [], fullName: [], company: [] });
  const [sortCategory, setSortCategory] = useState('');
  const [columnFilters, setColumnFilters] = useState(() => ({
    company: '',
    product: [],
    fullName: '',
    month: [],
    year: [],
  }));

  const { id: reportId } = useParams();
  const { query: rawUrlQuery } = useParams();
  const formattedQuery = queryStringToObject(rawUrlQuery);

  const {
    showChart,
    useDateSelectors,
    year: [defaultYear],
    month: [defaultMonth],
  } = formattedQuery;

  // eslint-disable-next-line max-len
  const { data: exportRuleData, isFetching: exportRuleFetching } = exportRuleQuery.useFindById({ id: reportId });
  const { data: allExportRuleData } = exportRuleQuery.useGetAll();
  const { data: companyData } = companyQuery.useGetAll();
  const { data: productData } = productQuery.useGetAll();
  const { data: allAgents } = agentQuery.useGetAgentsByQuery();
  const { mutateAsync: chartMutateAsync } = docsMutation.useExportChart();
  const { mutateAsync: tableMutateAsync } = docsMutation.useExportTable();
  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();
  // eslint-disable-next-line max-len
  const { data: agentData, isFetching: agentFetching } = agentQuery.useGetAgentById({ id: identifierId });
  const navigate = useNavigate();

  useEffect(() => {
    if (companyData) {
      const opts = companyData.map((c) => ({
        label: c.hebrew,
        value: c._id,
      }));
      setFilterOptions((prev) => ({ ...prev, company: opts }));
    }
  }, [companyData]);

  useEffect(() => {
    if (productData) {
      const opts = productData.map((p) => ({
        label: p.hebrew,
        value: p._id,
      }));
      setFilterOptions((prev) => ({ ...prev, product: opts }));
    }
  }, [productData]);

  useEffect(() => {
    if (allAgents) {
      const opts = allAgents.data.map((a) => ({
        label: a.personalDetails.fullName,
        value: a._id,
      }));
      setFilterOptions((prev) => ({ ...prev, fullName: opts }));
    }
  }, [allAgents]);

  useEffect(() => {
    const newFilters = {
      company: Array.isArray(formattedQuery.company) && formattedQuery.company[0] ? formattedQuery.company[0] : '',
      product: Array.isArray(formattedQuery.product) ? formattedQuery.product : [],
      // eslint-disable-next-line max-len
      month: Array.isArray(formattedQuery.month) && formattedQuery.month[0] ? [formattedQuery.month[0]] : [],
      year: Array.isArray(formattedQuery.year) ? formattedQuery.year : [],
      fullName: Array.isArray(formattedQuery.agent) && formattedQuery.agent[0] ? formattedQuery.agent[0] : '',
    };
    if (JSON.stringify(columnFilters) !== JSON.stringify(newFilters)) {
      setColumnFilters(newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  const showChartType = sortCategory === 'date';

  const buildChartQuery = (query, ruleId, category) => {
    if (isObjectEmpty(query)) return null;
    const {
      agent = [],
      year,
      month,
      company = [],
      product = [],
    } = query;

    return {
      exportRuleId: ruleId,
      year,
      month,
      identifiers: agent,
      query: {},
      additionalData: { companyId: company, productId: product },
      categoriesType: category,
    };
  };

  const onBarClick = (query, selectedCategory) => {
    let newCategory;
    let {
      company,
      product,
      year,
      month,
    } = query;

    const {
      category,
      agent,
    } = query;

    const {
      category: selectCategory,
      year: selectedYear,
      month: selectedMonth,
    } = selectedCategory;

    switch (category) {
      case 'company':
        company = companyData.find((c) => c.hebrew === selectCategory)?._id;
        year = selectedYear ? [selectedYear] : year;
        month = selectedMonth ? [selectedMonth] : month;
        newCategory = 'product';
        break;

      case 'product':
        product = productData.find((c) => c.hebrew === selectCategory)?._id;
        newCategory = company.length > 0 ? 'product' : 'company';
        break;

      case 'date':
        if (month.length === 0 && year.length > 0) {
          year = [selectCategory];
          month = [];
        } else {
          [month, year] = selectCategory.split('/');
          year = [year];
          month = [month];
        }
        newCategory = 'company';
        break;

      default:
        break;
    }

    const currentRule = allExportRuleData.find((rule) => rule._id === reportId);
    const ruleTitleId = currentRule.columns.find((col) => col.key)?.title?._id;
    const newRule = allExportRuleData.find((rule) => rule.category === newCategory
      && rule.columns.some((col) => col.key && col.title._id === ruleTitleId));

    const {
      _id: newRuleId,
      showChart: ruleShowChart,
      useDateSelectors: ruleUseDateSelectors,
    } = newRule;

    const updatedQuery = {
      ...query,
      company: Array.isArray(company) ? company : [company],
      product: Array.isArray(product) ? product : [product],
      year,
      month,
      agent,
    };

    const chartQuery = buildChartQuery(updatedQuery, newRuleId, newCategory);

    const path = chartQuery.identifiers.length > 1
      ? routes.reportList.path
      : routes.reportsView.path;

    const queryParams = objectToQueryString({
      month: chartQuery.month,
      year: chartQuery.year,
      company: chartQuery.additionalData.companyId,
      product: chartQuery.additionalData.productId,
      category: chartQuery.categoriesType,
      showChart: ruleShowChart,
      useDateSelectors: ruleUseDateSelectors,
      agent,
    });

    navigate(routeWithParams(path, {
      id: chartQuery.exportRuleId,
      query: queryParams,
    }));
  };

  const childProps = {
    isChartTypeActive: showChartType,
    noDataAction: 'addFiles',
    chartCategory: sortCategory,
    onBarClick,
    showTotalSum: true,
  };

  // eslint-disable-next-line consistent-return
  const getChartData = async (query) => {
    let returnData;

    if (!isObjectEmpty(query)) {
      const {
        agent,
        year,
        month,
        company,
        product,
        category,
      } = query;

      setSortCategory(category);
      const seriesFormat = { name: [] };
      let queryData;
      if (company.length !== 0 || category === 'date' || category === 'product') {
        seriesFormat.name.unshift('company');
        setSelectedCompany(company);
      }

      if (product.length !== 0 || category === 'company') {
        seriesFormat.name.unshift('product');
        setselectedProducts(product);
      }

      if (agent.length > 1) {
        seriesFormat.name.unshift({ name: 'personalDetails', value: ['uid', 'fullName'] });
      }

      if (seriesFormat.name.length === 0) {
        seriesFormat.name.push('primaryValue');
      }

      queryData = {
        year,
        month,
        identifiers: agent,
        query: {},
        additionalData: {
          companyId: company,
          productId: product,
        },
        categoriesType: category,
      };

      queryData = { exportRuleId: reportId, seriesFormat, ...queryData };

      try {
        returnData = await chartMutateAsync(queryData);
      } catch {
        returnData = { data: [], series: [] };
      }

      return returnData;
    }
  };

  const getTableData = async (start, end, query, sortStatus) => {
    let returnData = { data: [], series: [] };
    const {
      agent,
      year,
      month,
      category,
      company,
      product,
    } = query;

    const queryData = {
      year,
      month,
      identifiers: agent,
      query: {},
      additionalData: {
        companyId: query.company,
        productId: query.product,
      },
      exportRuleId: reportId,
      start,
      end,
      sort: sortStatus?.columnAccessor ? sortStatus : null,
    };

    try {
      const { data: mutateData, count } = await tableMutateAsync(queryData);
      const { rows, columns: dataColumns } = mutateData;

      // eslint-disable-next-line no-unused-vars
      const cleanColumns = dataColumns.map(({ filter, ...rest }) => rest);
      setColumns(cleanColumns);

      if (category === 'date') {
        const firstMonth = month[0];
        const lastMonth = month[month.length - 1];
        const firstYear = year[0];
        const lastYear = year[year.length - 1];

        setSelectedDate(`${formatMonthYearString(firstMonth, firstYear)} - ${formatMonthYearString(lastMonth, lastYear)} `);
      } else {
        setSelectedDate(`${month}/${year} `);
      }
      if (agent.length === 0) {
        setNoIdentifiers(true);
      } else {
        setIdentifierId(agent);
      }
      if (company.length !== 0) {
        setSelectedCompany(company);
      }

      if (product.length !== 0) {
        setselectedProducts(product);
      }
      returnData = { data: rows, count };
    } catch {
      returnData = { data: [], count: 0 };
    }

    return returnData;
  };

  // eslint-disable-next-line consistent-return
  const createFile = async (option, query) => {
    const { agent, year, month } = query;

    const exportData = {
      columns,
      query: {},
      fileType: option,
      service: 'docs',
      titleArray: ['personalDetails.fullName'],
      furtherData: {
        identifiers: agent,
        additionalData: {
          companyId: query.company,
          productId: query.product,
        },
        exportRuleId: reportId,
        year,
        month,
      },
    };

    const fileTitleBase = exportRuleData?.title;
    const fileTitle = option === 'pdf' ? `${fileTitleBase}.pdf` : `${fileTitleBase}.xlsx`;

    try {
      const fileData = await exportFileMutate(exportData);

      return { fileData, fileTitle };
    } catch (error) {
      notifications.show({
        title: t('pages.agents.notification.errortitle'),
        message: t('pages.agents.notification.errormessage'),
      });
      // eslint-disable-next-line no-console
      console.error('Error fetching users:', error);
    }
  };
  const fullName = noIdentifiers ? t('pages.reportView.allAgents') : agentData?.personalDetails?.fullName;

  const pageHeader = `${exportRuleData?.title}`;
  const companyName = selectedCompany?.map((id) => companyData?.find((company) => company._id === id)?.hebrew)?.filter(Boolean).join(', ') || t('pages.reportView.allCompanies');
  const productName = selectedProducts?.map((id) => productData?.find((product) => product._id === id)?.hebrew)?.filter(Boolean).join(', ') || t('pages.reportView.allProducts');

  // eslint-disable-next-line max-len
  const defaultFilters = useDateSelectors ? { year: String(defaultYear), month: String(defaultMonth) } : {};

  useEffect(() => {
    const columnsCopy = columns;
    const companySelect = companyData && companyData
      .map((company) => ({ label: company.hebrew, value: company._id }));
    const productSelect = productData && productData
      .map((product) => ({ label: product.hebrew, value: product._id }));
    const companyColumnIndex = columnsCopy.findIndex((column) => column.accessor === 'company');
    columnsCopy[companyColumnIndex] = { data: companySelect, ...columnsCopy[companyColumnIndex] };
    const productColumnIndex = columnsCopy.findIndex((column) => column.accessor === 'product');
    columnsCopy[productColumnIndex] = { data: productSelect, ...columnsCopy[productColumnIndex] };
    setColumns(columnsCopy);
  }, [companyData, productData, columns]);

  const companyTitle = `${companyName || t('global.allCompanies')}`;
  const productTitle = `${productName || t('global.allProducts')}`;
  const agentTitle = fullName || t('global.allAgents');
  const informationSubTitle = `${companyTitle} | ${productTitle} | ${agentTitle}`;

  const subTitle = [informationSubTitle, selectedDate];

  return (
    <>

      <PageTemplate
        title={(!exportRuleFetching && !agentFetching) && pageHeader}
        subtitle={subTitle}
        withBackButton = {true}
       >
         <Table
          isExportActive
          exportOptions={exportRuleData?.exportOptions}
          getData={getTableData}
          columns={columns}
          isColumnsActive={false}
          childElement={showChart ? ChartComponent : null}
          childGetData={getChartData}
          childProps={childProps}
          noDataAction='addFiles'
          useDateSelectors={useDateSelectors}
          defaultFilters={defaultFilters}
          emailtemplateId={exportRuleData?.emailTemplate}
          createFile={createFile}
          showSelectionColumn={false}
          serverPagination={true}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          isUrlChange={true}
          filterOptions={filterOptions}
          height ={'35rem'}
        />
      </PageTemplate>
    </>
  );
};

export default ReportView;
